'use client'

import type { VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { inputVariants } from '@cocoonspace/ui/components/input'
import { cn } from '@cocoonspace/ui/lib/utils'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { Select as SelectPrimitive } from 'radix-ui'

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
		VariantProps<typeof inputVariants>
>(({ variant, inputSize, className, children, ...props }, ref) => (
	<SelectPrimitive.Trigger
		data-slot='select-trigger'
		className={cn(
			inputVariants({ inputSize, variant }),
			'flex items-center justify-between gap-2 pr-4 whitespace-nowrap [&>span]:line-clamp-1',
			className,
		)}
		ref={ref}
		{...props}
	>
		{children}

		<SelectPrimitive.Icon asChild>
			<ChevronDownIcon
				className='font-base mt-0.5 ml-auto'
				strokeWidth={1}
			/>
		</SelectPrimitive.Icon>
	</SelectPrimitive.Trigger>
))

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollUpButton
		className={cn(
			'flex cursor-default items-center justify-center py-1',
			className,
		)}
		ref={ref}
		{...props}
	>
		<ChevronUpIcon />
	</SelectPrimitive.ScrollUpButton>
))

SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollDownButton
		className={cn(
			'flex cursor-default items-center justify-center py-1',
			className,
		)}
		ref={ref}
		{...props}
	>
		<ChevronDownIcon />
	</SelectPrimitive.ScrollDownButton>
))

SelectScrollDownButton.displayName =
	SelectPrimitive.ScrollDownButton.displayName

export const SelectContentWithoutPortal = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
	<SelectPrimitive.Content
		className={cn(
			'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-32 overflow-hidden rounded-md shadow-lg',
			position === 'popper' &&
				'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
			className,
		)}
		position={position}
		ref={ref}
		{...props}
	>
		<SelectScrollUpButton />

		<SelectPrimitive.Viewport
			className={cn(
				'p-1',
				position === 'popper' &&
					'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)',
			)}
		>
			{children}
		</SelectPrimitive.Viewport>

		<SelectScrollDownButton />
	</SelectPrimitive.Content>
))

SelectContentWithoutPortal.displayName = 'SelectContentWithoutPortal'

const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>((props, ref) => (
	<SelectPrimitive.Portal>
		<SelectContentWithoutPortal
			ref={ref}
			{...props}
		/>
	</SelectPrimitive.Portal>
))

SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		className={cn('px-2 py-1.5 text-sm font-semibold', className)}
		ref={ref}
		{...props}
	/>
))

SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Item
		className={cn(
			'focus:bg-accent focus:text-accent-foreground relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50',
			className,
		)}
		ref={ref}
		{...props}
	>
		<span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
			<SelectPrimitive.ItemIndicator>
				<CheckIcon />
			</SelectPrimitive.ItemIndicator>
		</span>

		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
))

SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		className={cn('bg-muted -mx-1 my-1 h-px', className)}
		ref={ref}
		{...props}
	/>
))

SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
